import React, { useState, useEffect } from 'react';
import { callApi } from '../api/api';

const HealthInsuranceFormView = ({ showPage, userName, userFamilyMembers, showMessage }) => {
    const [members, setMembers] = useState(() => {
        if (userFamilyMembers && userFamilyMembers.length > 0) {
            return userFamilyMembers.map(fm => ({ name: fm.name, dob: fm.dob, relation: fm.relation, isSaved: true }));
        }
        return [{ name: userName || '', dob: '', relation: 'Self', isSaved: false }];
    });
    const [loading, setLoading] = useState(false);
    const [savingIndex, setSavingIndex] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (index, field, value) => {
        const updatedMembers = [...members];
        if (field === 'dob') {
            let val = value.replace(/\D/g, '');
            if (val.length > 4) {
                val = val.substring(0, 2) + '/' + val.substring(2, 4) + '/' + val.substring(4, 8);
            } else if (val.length > 2) {
                val = val.substring(0, 2) + '/' + val.substring(2, 4);
            }
            updatedMembers[index][field] = val;
        } else {
            updatedMembers[index][field] = value;
        }
        updatedMembers[index].isSaved = false; // Reset saved status on change
        setMembers(updatedMembers);
    };

    const handleAddMember = () => {
        setMembers([...members, { name: '', dob: '', relation: 'Other', isSaved: false }]);
    };

    const handleRemoveMember = (index) => {
        if (members.length > 1) {
            const updatedMembers = members.filter((_, i) => i !== index);
            setMembers(updatedMembers);
        }
    };

    const handleSaveProfile = async (index) => {
        const member = members[index];
        if (!member.name || !member.dob || !member.relation) {
            if (showMessage) showMessage('Please fill all fields before saving to profile', 'error');
            return;
        }
        
        setSavingIndex(index);
        try {
            const response = await callApi('/users/profile/family', 'POST', member);
            if (response.success) {
                if (showMessage) showMessage(`${member.name} saved to your profile!`, 'success');
                const updatedMembers = [...members];
                updatedMembers[index].isSaved = true;
                setMembers(updatedMembers);
            } else {
                if (showMessage) showMessage(response.message || 'Failed to save', 'error');
            }
        } catch (err) {
            console.error(err);
            if (showMessage) showMessage('Error saving to profile', 'error');
        }
        setSavingIndex(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        // Validate all members
        for (let m of members) {
            if (!m.name || !m.dob || !m.relation) {
                setError('Please fill all fields for all members');
                return;
            }
        }

        setLoading(true);

        try {
            const response = await callApi('/insurance/leads', 'POST', { members });
            
            if (response.success) {
                // Return to home page internally
                showPage('landing');
            } else {
                setError(response.message || 'Failed to submit form');
                setLoading(false);
            }
        } catch (err) {
            console.error('Error submitting insurance lead:', err);
            setError('Server error. Please try again.');
            setLoading(false);
        }
    };

    return (
        <>
        {/* Carousel of partners */}
        <div className="max-w-5xl mx-auto overflow-hidden mt-2 mb-2 px-2">
            <h4 className="text-center text-gray-500 font-semibold mb-3 sm:mb-4 uppercase tracking-wider text-xs">Our Leading Insurance Partners</h4>
            <div className="relative flex overflow-x-hidden group">
                <div className="animate-marquee whitespace-nowrap flex items-center">
                    {/* First set */}
                    {[
                        { name: 'Star Health', domain: 'starhealth.in', color: 'text-red-600', font: 'font-bold tracking-tight' },
                        { name: 'HDFC Ergo', domain: 'hdfcergo.com', color: 'text-red-700', font: 'font-extrabold tracking-tight' },
                        { name: 'Care Insurance', domain: 'careinsurance.com', color: 'text-blue-600', font: 'font-bold' },
                        { name: 'Niva Bupa', domain: 'nivabupa.com', color: 'text-orange-500', font: 'font-bold italic' },
                        { name: 'ICICI Lombard', domain: 'icicilombard.com', color: 'text-blue-800', font: 'font-black uppercase tracking-tighter' },
                        { name: 'Bajaj Allianz', domain: 'bajajallianz.com', color: 'text-sky-600', font: 'font-bold tracking-wide' },
                        { name: 'SBI General', domain: 'sbigeneral.in', color: 'text-indigo-600', font: 'font-bold' },
                    ].map((partner, i) => (
                        <div key={i} className="mx-4 sm:mx-8 flex flex-row items-center justify-center gap-2 sm:gap-3 opacity-80 hover:opacity-100 transition-opacity">
                            <img 
                                src={`https://logo.clearbit.com/${partner.domain}`} 
                                alt={partner.name} 
                                className="h-6 w-6 sm:h-8 sm:w-8 object-contain rounded-full shadow-sm bg-white"
                                onError={(e) => { e.target.onerror = null; e.target.src = `https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${partner.domain}&size=128`; }}
                            />
                            <span className={`text-sm sm:text-lg ${partner.color} ${partner.font}`}>{partner.name}</span>
                        </div>
                    ))}
                    
                    {/* Duplicate set for seamless scrolling */}
                    {[
                        { name: 'Star Health', domain: 'starhealth.in', color: 'text-red-600', font: 'font-bold tracking-tight' },
                        { name: 'HDFC Ergo', domain: 'hdfcergo.com', color: 'text-red-700', font: 'font-extrabold tracking-tight' },
                        { name: 'Care Insurance', domain: 'careinsurance.com', color: 'text-blue-600', font: 'font-bold' },
                        { name: 'Niva Bupa', domain: 'nivabupa.com', color: 'text-orange-500', font: 'font-bold italic' },
                        { name: 'ICICI Lombard', domain: 'icicilombard.com', color: 'text-blue-800', font: 'font-black uppercase tracking-tighter' },
                        { name: 'Bajaj Allianz', domain: 'bajajallianz.com', color: 'text-sky-600', font: 'font-bold tracking-wide' },
                        { name: 'SBI General', domain: 'sbigeneral.in', color: 'text-indigo-600', font: 'font-bold' },
                    ].map((partner, i) => (
                        <div key={`dup-${i}`} className="mx-4 sm:mx-8 flex flex-row items-center justify-center gap-2 sm:gap-3 opacity-80 hover:opacity-100 transition-opacity">
                            <img 
                                src={`https://logo.clearbit.com/${partner.domain}`} 
                                alt={partner.name} 
                                className="h-6 w-6 sm:h-8 sm:w-8 object-contain rounded-full shadow-sm bg-white"
                                onError={(e) => { e.target.onerror = null; e.target.src = `https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${partner.domain}&size=128`; }}
                            />
                            <span className={`text-sm sm:text-lg ${partner.color} ${partner.font}`}>{partner.name}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        <div className="max-w-xl mx-auto bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8 mx-3 sm:mx-auto">
            <div className="bg-sky-600 p-4 sm:p-6 text-center text-white">
                <i className="fas fa-shield-heart text-4xl sm:text-5xl mb-2 sm:mb-3"></i>
                <h2 className="text-xl sm:text-2xl font-bold">Health Insurance</h2>
                <p className="text-sky-100 text-xs sm:text-sm mt-1">Get the best coverage for your loved ones.</p>
            </div>
            
            <div className="p-4 sm:p-8">
                <p className="text-gray-600 mb-6 text-center text-sm">
                    Please provide the details of all individuals requiring health insurance, and our team will get back to you with the best options.
                </p>

                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-4 flex items-center gap-2">
                        <i className="fas fa-exclamation-circle"></i> {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    
                    {/* Render Table for saved members */}
                    {members.filter(m => m.isSaved).length > 0 && (
                        <div className="mb-6">
                            {/* Desktop Table */}
                            <div className="hidden sm:block overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="py-3 px-4 text-sm font-semibold text-gray-700">Full Name</th>
                                            <th className="py-3 px-4 text-sm font-semibold text-gray-700">DOB</th>
                                            <th className="py-3 px-4 text-sm font-semibold text-gray-700">Relation</th>
                                            <th className="py-3 px-4 text-sm font-semibold text-gray-700 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {members.map((member, index) => {
                                            if (!member.isSaved) return null;
                                            return (
                                                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                                                    <td className="py-3 px-4 text-sm text-gray-800 font-medium">{member.name}</td>
                                                    <td className="py-3 px-4 text-sm text-gray-600">{member.dob}</td>
                                                    <td className="py-3 px-4 text-sm text-gray-600">{member.relation}</td>
                                                    <td className="py-3 px-4 text-sm text-right">
                                                        <button type="button" onClick={() => handleChange(index, 'isSaved', false)} className="text-sky-600 hover:text-sky-800 mr-4" title="Edit">
                                                            <i className="fas fa-edit"></i> Edit
                                                        </button>
                                                        <button type="button" onClick={() => handleRemoveMember(index)} className="text-red-500 hover:text-red-700" title="Delete">
                                                            <i className="fas fa-trash"></i> Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {/* Mobile Card View */}
                            <div className="sm:hidden space-y-3">
                                {members.map((member, index) => {
                                    if (!member.isSaved) return null;
                                    return (
                                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex justify-between items-start gap-3">
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-gray-800 text-sm truncate">{member.name}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">{member.relation} &bull; {member.dob}</p>
                                            </div>
                                            <div className="flex gap-3 shrink-0">
                                                <button type="button" onClick={() => handleChange(index, 'isSaved', false)} className="text-sky-600 hover:text-sky-800 text-sm" title="Edit">
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button type="button" onClick={() => handleRemoveMember(index)} className="text-red-500 hover:text-red-700 text-sm" title="Delete">
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Render Form boxes for unsaved members */}
                    {members.map((member, index) => {
                        if (member.isSaved) return null;
                        return (
                            <div key={index} className="p-5 border border-gray-200 rounded-lg bg-gray-50 relative">
                            {members.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => handleRemoveMember(index)}
                                    className="absolute top-3 right-3 text-red-500 hover:text-red-700 p-1"
                                    title="Remove person"
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            )}
                            <h3 className="font-bold text-gray-700 mb-4 border-b pb-2">Person {index + 1}</h3>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                    <input
                                        type="text"
                                        value={member.name}
                                        onChange={(e) => handleChange(index, 'name', e.target.value)}
                                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 outline-none transition-all"
                                        placeholder="e.g. Rahul Sharma"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                                    <input
                                        type="text"
                                        placeholder="dd/mm/yyyy"
                                        maxLength="10"
                                        value={member.dob}
                                        onChange={(e) => handleChange(index, 'dob', e.target.value)}
                                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 outline-none transition-all"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Relation</label>
                                    <select
                                        value={member.relation}
                                        onChange={(e) => handleChange(index, 'relation', e.target.value)}
                                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 outline-none transition-all"
                                    >
                                        <option value="Self">Self</option>
                                        <option value="Spouse">Spouse</option>
                                        <option value="Son">Son</option>
                                        <option value="Daughter">Daughter</option>
                                        <option value="Father">Father</option>
                                        <option value="Mother">Mother</option>
                                        <option value="Brother">Brother</option>
                                        <option value="Sister">Sister</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>

                            <div className="mt-4 flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => handleSaveProfile(index)}
                                    disabled={member.isSaved || savingIndex === index}
                                    title="Save to Profile"
                                    className={`p-2 rounded-full text-lg transition-colors flex items-center justify-center w-10 h-10 ${
                                        member.isSaved
                                            ? 'bg-sky-100 text-sky-700 cursor-not-allowed border border-sky-200'
                                            : 'bg-sky-50 text-sky-600 hover:bg-sky-100 border border-sky-200'
                                    }`}
                                >
                                    {savingIndex === index ? (
                                        <i className="fas fa-spinner fa-spin"></i>
                                    ) : member.isSaved ? (
                                        <i className="fas fa-check"></i>
                                    ) : (
                                        <i className="fas fa-save"></i>
                                    )}
                                </button>
                            </div>
                        </div>
                        );
                    })}

                    <button
                        type="button"
                        onClick={handleAddMember}
                        className="w-full py-2.5 rounded-lg text-sky-600 bg-sky-50 font-semibold border border-sky-200 hover:bg-sky-100 transition-colors flex justify-center items-center gap-2"
                    >
                        <i className="fas fa-plus-circle"></i> Add Another Person
                    </button>

                    <div className="pt-4 border-t border-gray-100 flex flex-col gap-3">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-3 rounded-lg text-white font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                                loading ? 'bg-sky-400 cursor-not-allowed' : 'bg-sky-600 hover:bg-sky-700 shadow-md hover:shadow-lg'
                            }`}
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i> Processing...
                                </>
                            ) : (
                                <>
                                    Submit Request <i className="fas fa-arrow-right"></i>
                                </>
                            )}
                        </button>
                        
                        <div className="relative flex items-center justify-center my-1">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                            <div className="relative bg-white px-4 text-sm font-bold text-slate-400">OR</div>
                        </div>

                        <button
                            type="button"
                            onClick={() => window.open('https://wa.me/919029122955?text=Hi,%20I%20am%20interested%20in%20Health%20Insurance', '_blank')}
                            className="w-full bg-[#25D366] text-white font-bold py-3 rounded-lg shadow-md hover:bg-[#128C7E] hover:shadow-lg transition-all flex items-center justify-center gap-2 text-lg"
                        >
                            <i className="fab fa-whatsapp text-2xl"></i> WhatsApp Us
                        </button>
                    </div>
                </form>
            </div>
        </div>



        </>
    );
};

export default HealthInsuranceFormView;
